import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type TConversationProvider =
  | 'ultravox'
  | 'heygen'
  | 'livekit'
  | 'livekit_ru'
  | 'liveavatar'
  | 'yandex';

// Types based on the OpenAPI schema
export interface UserListItemResponse {
  id: string;
  created_at: string;
  email: string;
  first_name: string;
  last_name: string;
  role_name: string;
  enabled: boolean;
  organization_id: string;
  organization_name: string;
  phone_number: string | null;
  employee_number: string | null;
  city: string | null;
  company: string | null;
  position: string | null;
}

export interface UserListResponse {
  total: number;
  limit: number;
  page: number;
  has_more: boolean;
  users: UserListItemResponse[];
}

export interface UserListQueryParams {
  page?: number;
  limit?: number;
  organization_id?: string;
  roles?: string[];
  enabled?: boolean;
  search?: string;
  column_key?: string;
  order?: string;
}

export interface UserResponse {
  id: string;
  created_at: string;
  email: string;
  first_name: string;
  last_name: string;
  role_name: string;
  organization_id: string;
  enabled: boolean;
  phone_number: string | null;
  employee_number: string | null;
  city: string | null;
  company: string | null;
  position: string | null;
}

export interface UserCreateRequest {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  role_name: string;
  organization_id: string;
  organization_name: string;
}

export interface UserUpdateRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  role_id?: string;
  role_name?: string;
  enabled?: boolean;
  organization_id?: string;
}

export interface UserEnabledUpdateRequest {
  enabled: boolean;
}

export interface SubscriptionSettings {
  show_lite_plan: boolean;
  show_advanced_plan: boolean;
  show_enterprise_plan: boolean;
  allow_annual_payment: boolean;
  allow_monthly_payment: boolean;
  default_payment_option: 'monthly' | 'annual';
}

export interface OrganizationBase {
  _id: string;
  name: string;
  trial_available: boolean;
  is_virtual: boolean;
  is_partner: boolean;
  conversation_provider: TConversationProvider;
  video_recording_enabled: boolean;
  subscription_settings?: SubscriptionSettings;
}

export interface OrganizationDetail extends OrganizationBase {
  description: string;
  public_alias: string | null;
  subscription_settings: SubscriptionSettings;
  signed_avatar_url?: string;
}

export interface OrganizationImageUpload {
  file_name?: string;
  mime_type: string;
  object_key: string;
}

export interface OrganizationCreateUpdate {
  name: string;
  description: string;
  public_alias: string;
  trial_available: boolean;
  conversation_provider: TConversationProvider;
  is_partner: boolean;
  avatar_upload?: OrganizationImageUpload;
  video_recording_enabled: boolean;
  subscription_settings: SubscriptionSettings;
}

export interface SubscriptionPeriodSummary {
  id: string;
  subscription_plan_name: string;
  organization_name: string;
  total_excluding_tax: number;
  subtype: string;
  period_end: string;
  period_start: string;
  payment_received: boolean;
  purpose_type: string;
  project_limit: number;
  session_limit: number;
}

export interface SubscriptionPeriodCreate {
  subscription_plan_id: string;
  subscription_plan_name: string;
  organization_id: string;
  organization_name: string;
  total_excluding_tax: number;
  period_start: string;
  period_end: string;
  payment_received: boolean;
  type: string;
  purpose_type: string;
  project_limit: number;
  session_limit: number;
}

export interface SubscriptionPeriodUpdate {
  subscription_plan_id?: string;
  subscription_plan_name?: string;
  total_excluding_tax?: number;
  period_start?: string;
  period_end?: string;
  payment_received: boolean;
  type?: string;
  purpose_type?: string;
  project_limit?: number;
  session_limit?: number;
}

export interface ManualDeduction {
  id: string;
  subscription_period_id: string;
  organization_name: string;
  subscription_plan_name: string | null;
  period_start: number;
  period_end: number;
  quantity: number;
  description: string;
  written_off_at: string;
  created_by_user_id: string;
  created_by_name: string;
}

export interface ManualDeductionCreate {
  subscription_period_id: string;
  quantity: number;
  description: string;
  written_off_at: string;
}

export interface ManualDeductionUpdate {
  quantity?: number;
  description?: string;
  written_off_at?: string;
}

export interface ManualDeductionsQuery {
  page: number;
  per_page: number;
  from?: string;
  to?: string;
}

export interface ManualDeductionsListResponse {
  documents: ManualDeduction[];
  total: number;
  limit: number;
  page: number;
  has_more: boolean;
}

export interface SelectBase {
  _id: string;
  name: string;
}

export interface CommunityAnalyticsAuthor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface CommunityAnalyticsItem {
  id: string;
  project_id: string;
  organization_id: string;
  title: string;
  organization_name: string;
  avatar_url: string;
  author: CommunityAnalyticsAuthor;
  participant_count: number;
  session_count: number;
  completion_rate: number;
  success_rate: number;
  score: number;
  saves_count: number;
}

export interface CommunityAnalyticsResponse {
  documents: CommunityAnalyticsItem[];
  total: number;
  limit: number;
  page: number;
  has_more: boolean;
}

export interface CommunityAnalyticsProjectSummary {
  id: string;
  project_id: string;
  title: string;
  description: string;
  avatar_url: string;
  author: CommunityAnalyticsAuthor;
  participant_count: number;
  session_count: number;
  completion_rate: number;
  success_rate: number;
  score: number;
  saves_count: number;
}

export type CommunityAnalyticsSessionStatus =
  | 'COMPLETED_WITH_REPORT'
  | 'ENGAGED_NO_REPORT'
  | 'VIEWED_NOT_STARTED';

export interface CommunityAnalyticsProjectSession {
  id: string;
  organization_id: string;
  organization_name: string;
  participant_first_name: string | null;
  participant_last_name: string | null;
  created_at: string;
  duration_seconds: number;
  score: number;
  status: CommunityAnalyticsSessionStatus;
}

export interface CommunityAnalyticsProjectSessionsResponse {
  documents: CommunityAnalyticsProjectSession[];
  total: number;
  limit: number;
  page: number;
  has_more: boolean;
}

export interface CommunityAnalyticsProjectSessionsQuery {
  projectId: string;
  page: number;
  limit: number;
  column_key: string;
  order: 'asc' | 'desc';
  created_from?: string;
  created_to?: string;
  statuses: CommunityAnalyticsSessionStatus[];
}

export interface CommunityAnalyticsProjectSessionsExportQuery {
  projectId: string;
  column_key: string;
  order: 'asc' | 'desc';
  created_from?: string;
  created_to?: string;
  statuses: CommunityAnalyticsSessionStatus[];
  locale: string;
  time_zone: string;
}

export interface CommunityAnalyticsQuery {
  organization_ids: string[];
  author_ids: string[];
  created_from?: string;
  created_to?: string;
  page: number;
  limit: number;
  column_key: string;
  order: 'asc' | 'desc';
}

export interface CommunityAnalyticsExportQuery {
  organization_ids: string[];
  author_ids: string[];
  created_from?: string;
  created_to?: string;
  locale: string;
}

export interface CommunityAnalyticsOrganization {
  id: string;
  organization_id: string;
  name: string;
  avatar_url: string | null;
}

export interface ProjectTransferRequest {
  organization_id: string;
  user_id: string;
}

export interface ProjectTransferResponse {
  id: string;
  conversation_id: string;
}

export type IdeaAccessibility = 'public' | 'private';
export type IdeaOrder =
  | 'CREATED_DESC'
  | 'CREATED_ASC'
  | 'VOTES_DESC'
  | 'VOTES_ASC'
  | 'RANDOM';
export type IdeaStatusId = 1 | 2 | 3 | 4 | 5;
export type IdeaCategoryId =
  | 'scenario_creation'
  | 'scenario_access'
  | 'roleplay_passing'
  | 'results_and_reports'
  | 'other';

export interface IdeaRecord {
  id: string;
  title: string;
  description: string;
  dev_comment: string | null;
  accessibility_id: IdeaAccessibility;
  note: string | null;
  costs: number | null;
  status_id: IdeaStatusId;
  category_id: IdeaCategoryId | null;
  organization_id: string;
  user_id: string;
  i_have_vote: boolean | null;
  votes_count: number | null;
  criticality_sum: number | null;
  organization_votes_count: number | null;
  avg_criticality: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface IdeasQueryParams {
  search?: string;
  status_id?: IdeaStatusId;
  category_id?: IdeaCategoryId;
  organization_id?: string;
  user_id?: string;
  accessibility_id?: IdeaAccessibility;
  order?: IdeaOrder;
  display_mode?: 'ALL_IDEAS' | 'ONLY_MY_IDEAS';
  page?: number;
  limit?: number;
}

export interface IdeasListResponse {
  items: IdeaRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface IdeaUpdateRequest {
  title?: string;
  description?: string;
  dev_comment?: string | null;
  accessibility_id?: IdeaAccessibility;
  note?: string | null;
  costs?: number | null;
  status_id?: IdeaStatusId;
  category_id?: IdeaCategoryId | null;
}

export interface IdeaVoteAnswer {
  id: 'importance_1' | 'importance_2' | 'importance_3';
  importance: number;
  value: string;
}

export interface IdeaVoteRecord {
  id: string;
  idea_id: string;
  answer_id: 'importance_1' | 'importance_2' | 'importance_3';
  description: string;
  organization_id: string;
  user_id: string;
  created_at: string;
  updated_at: string | null;
  answer: IdeaVoteAnswer | null;
}

export interface IdeaVotesListResponse {
  items: IdeaVoteRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface TariffUsageSummary {
  id: string;
  organization_id: string;
  organization_name: string;
  purpose_type: string;
  period_start: number;
  period_end: number;
  session_limit: number | null;
  sessions_used: number;
}

export interface ActivityUsageOrganizationRow {
  organization_id: string;
  organization_name: string;
  sessions_by_date: Record<string, number>;
  total_sessions: number;
}

export interface ActivityUsageResponse {
  purpose_type: string;
  dates: string[];
  organizations: ActivityUsageOrganizationRow[];
}

export interface IReleaseNoteTextContent {
  type: 'text';
  content: string;
}

export type TReleaseNoteItem = {
  position: number;
} & IReleaseNoteTextContent;

export interface IGetReleaseNote {
  id: string;
  title: string;
  publication_at: string;
  items: TReleaseNoteItem[];
}

export interface IGetReleaseNotesTableItem {
  id: string;
  title: string;
  publication_at: string;
}

export interface IUpdateReleaseNote {
  title: string;
  publication_at: string;
  items: TReleaseNoteItem[];
}

export interface ReportGenerationAdminSessionRow {
  session_id: string;
  project_id: string;
  project_title?: string | null;
  organization_id: string;
  user_id?: string | null;
  user_full_name?: string | null;
  session_status?: string | null;
  last_activity_at?: string | null;
  finalized_at?: string | null;
  finalized_reason?: string | null;
  request_id?: string | null;
  job_id?: string | null;
  request_status?: string | null;
  job_status?: string | null;
  trigger_type?: string | null;
  has_report: boolean;
  report_id?: string | null;
  score?: number | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReportGenerationAdminSessionListResponse {
  items: ReportGenerationAdminSessionRow[];
  total: number;
  limit: number;
  offset: number;
}

// Create the API client
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_APP_API_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem('auth_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: [
    'Users',
    'Organizations',
    'SubscriptionPeriods',
    'ManualDeductions',
    'Selects',
    'Ideas',
    'ReleaseNotes',
    'ReportGeneration',
  ],
  endpoints: (builder) => ({
    // Users
    getUsers: builder.query<UserListResponse, UserListQueryParams | void>({
      query: (params) => ({
        url: '/users/',
        params: params ?? undefined,
      }),
      providesTags: ['Users'],
    }),
    getUser: builder.query<UserResponse, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    createUser: builder.mutation<void, UserCreateRequest>({
      query: (body) => ({
        url: '/users/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation<void, { id: string; body: UserUpdateRequest }>(
      {
        query: ({ id, body }) => ({
          url: `/users/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'Users', id },
          { type: 'Users' },
        ],
      },
    ),
    updateEnabledUser: builder.mutation<
      void,
      { id: string; body: UserEnabledUpdateRequest }
    >({
      query: ({ id, body }) => ({
        url: `/users/${id}/enabled`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Users', id },
        { type: 'Users' },
      ],
    }),

    // Organizations
    getOrganizations: builder.query<OrganizationBase[], void>({
      query: () => '/organizations/',
      providesTags: ['Organizations'],
    }),
    getOrganization: builder.query<OrganizationDetail, string>({
      query: (id) => `/organizations/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Organizations', id }],
    }),
    createOrganization: builder.mutation<
      { id: string },
      OrganizationCreateUpdate
    >({
      query: (body) => ({
        url: '/organizations/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: builder.mutation<
      { id: string },
      { id: string; body: OrganizationCreateUpdate }
    >({
      query: ({ id, body }) => ({
        url: `/organizations/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Organizations', id },
        { type: 'Organizations' },
      ],
    }),
    createOrganizationImageUploadTarget: builder.mutation<
      {
        provider: string;
        method: string;
        upload_url: string;
        upload_headers: Record<string, string>;
        object_key: string;
        expires_at: string;
      },
      { id: string; mime_type: string; file_name: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/organizations/${id}/avatar/upload-target`,
        method: 'POST',
        body,
      }),
    }),
    deleteOrganizationImage: builder.mutation<void, string>({
      query: (id) => ({
        url: `/organizations/${id}/avatar`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Organizations', id },
        { type: 'Organizations' },
      ],
    }),

    // Subscription Periods
    getSubscriptionPeriods: builder.query<SubscriptionPeriodSummary[], void>({
      query: () => '/subscription_periods/',
      providesTags: ['SubscriptionPeriods'],
    }),
    getSubscriptionPeriod: builder.query<SubscriptionPeriodSummary, string>({
      query: (id) => `/subscription_periods/${id}`,
      providesTags: (_result, _error, id) => [
        { type: 'SubscriptionPeriods', id },
      ],
    }),
    createSubscriptionPeriod: builder.mutation<void, SubscriptionPeriodCreate>({
      query: (body) => ({
        url: '/subscription_periods/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SubscriptionPeriods'],
    }),
    updateSubscriptionPeriod: builder.mutation<
      void,
      { id: string; body: SubscriptionPeriodUpdate }
    >({
      query: ({ id, body }) => ({
        url: `/subscription_periods/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'SubscriptionPeriods', id },
        { type: 'SubscriptionPeriods' },
      ],
    }),
    deleteSubscriptionPeriod: builder.mutation<void, string>({
      query: (id) => ({
        url: `/subscription_periods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubscriptionPeriods'],
    }),
    getTariffUsage: builder.query<TariffUsageSummary[], void>({
      query: () => '/admin/tariff-usage',
    }),
    getManualDeductions: builder.query<ManualDeductionsListResponse, ManualDeductionsQuery>({
      query: (params) => ({
        url: '/admin/manual-deductions/',
        params,
      }),
      providesTags: ['ManualDeductions'],
    }),
    createManualDeduction: builder.mutation<ManualDeduction, ManualDeductionCreate>({
      query: (body) => ({
        url: '/admin/manual-deductions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ManualDeductions'],
    }),
    updateManualDeduction: builder.mutation<
      ManualDeduction,
      { id: string; body: ManualDeductionUpdate }
    >({
      query: ({ id, body }) => ({
        url: `/admin/manual-deductions/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ManualDeductions'],
    }),
    deleteManualDeduction: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/admin/manual-deductions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ManualDeductions'],
    }),
    getActivityUsage: builder.query<ActivityUsageResponse, 'paid' | 'trial'>({
      query: (purposeType) => `/admin/activity-usage/${purposeType}`,
    }),
    transferProject: builder.mutation<
      ProjectTransferResponse,
      { projectId: string; body: ProjectTransferRequest }
    >({
      query: ({ projectId, body }) => ({
        url: `/admin/projects/${projectId}/transfer`,
        method: 'POST',
        body,
      }),
    }),

    // ReleaseNotes
    getReleaseNotes: builder.query<IGetReleaseNote[], void>({
      query: () => '/release_notes/',
      providesTags: ['ReleaseNotes'],
    }),
    getReleaseNotesTableList: builder.query<IGetReleaseNotesTableItem[], void>({
      query: () => '/release_notes/table-list',
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({
                type: 'ReleaseNotes' as const,
                id: item.id,
              })),
              { type: 'ReleaseNotes', id: 'LIST' },
            ]
          : [{ type: 'ReleaseNotes', id: 'LIST' }],
    }),
    getReleaseNote: builder.query<IGetReleaseNote, string>({
      query: (id) => `/release_notes/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'ReleaseNotes', id }],
    }),
    createReleaseNote: builder.mutation<void, IUpdateReleaseNote>({
      query: (body) => ({
        url: '/release_notes/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReleaseNotes'],
    }),
    updateReleaseNote: builder.mutation<
      void,
      { id: string; body: IUpdateReleaseNote }
    >({
      query: ({ id, body }) => ({
        url: `/release_notes/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ReleaseNotes', id },
        { type: 'ReleaseNotes' },
      ],
    }),
    deleteReleaseNote: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/release_notes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'ReleaseNotes', id },
        { type: 'ReleaseNotes', id: 'LIST' },
      ],
    }),
    getReportGenerationSessions: builder.query<
      ReportGenerationAdminSessionListResponse,
      { search?: string; limit?: number; offset?: number } | void
    >({
      query: (params) => ({
        url: '/report-generation/admin/sessions',
        params: params ?? undefined,
      }),
      providesTags: ['ReportGeneration'],
    }),
    runSessionReportGeneration: builder.mutation<void, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/report-generation/sessions/${sessionId}/run`,
        method: 'POST',
        body: {
          trigger_type: 'manual',
          force: false,
        },
      }),
      invalidatesTags: ['ReportGeneration'],
    }),
    rerunSessionReportGeneration: builder.mutation<void, { sessionId: string }>(
      {
        query: ({ sessionId }) => ({
          url: `/report-generation/sessions/${sessionId}/rerun`,
          method: 'POST',
        }),
        invalidatesTags: ['ReportGeneration'],
      },
    ),
    cancelSessionReportGeneration: builder.mutation<
      void,
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: `/report-generation/sessions/${sessionId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['ReportGeneration'],
    }),

    // Ideas
    getIdeas: builder.query<IdeasListResponse, IdeasQueryParams | void>({
      query: (params) => ({
        url: '/ideas/',
        params: params ?? undefined,
      }),
      transformResponse: (response: IdeaRecord[], meta): IdeasListResponse => ({
        items: response,
        total: Number(
          meta?.response?.headers.get('X-Total-Count') ?? response.length,
        ),
        page: Number(meta?.response?.headers.get('X-Page') ?? 1),
        limit: Number(
          meta?.response?.headers.get('X-Limit') ?? response.length,
        ),
        hasMore:
          (meta?.response?.headers.get('X-Has-More') ?? 'false') === 'true',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Ideas' as const, id })),
              { type: 'Ideas' as const, id: 'LIST' },
            ]
          : [{ type: 'Ideas' as const, id: 'LIST' }],
    }),
    getIdea: builder.query<IdeaRecord, string>({
      query: (id) => `/ideas/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Ideas', id }],
    }),
    getIdeaVotes: builder.query<
      IdeaVotesListResponse,
      { ideaId: string; page?: number; limit?: number }
    >({
      query: ({ ideaId, page = 1, limit = 20 }) => ({
        url: `/ideas/${ideaId}/votes`,
        params: { page, limit },
      }),
      transformResponse: (
        response: IdeaVoteRecord[],
        meta,
      ): IdeaVotesListResponse => ({
        items: response,
        total: Number(
          meta?.response?.headers.get('X-Total-Count') ?? response.length,
        ),
        page: Number(meta?.response?.headers.get('X-Page') ?? 1),
        limit: Number(
          meta?.response?.headers.get('X-Limit') ?? response.length,
        ),
        hasMore:
          (meta?.response?.headers.get('X-Has-More') ?? 'false') === 'true',
      }),
    }),
    updateIdea: builder.mutation<
      { id: string },
      { id: string; body: IdeaUpdateRequest }
    >({
      query: ({ id, body }) => ({
        url: `/ideas/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Ideas', id },
        { type: 'Ideas', id: 'LIST' },
      ],
    }),
    deleteIdea: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/ideas/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Ideas', id },
        { type: 'Ideas', id: 'LIST' },
      ],
    }),

    getCommunityAnalytics: builder.query<
      CommunityAnalyticsResponse,
      CommunityAnalyticsQuery
    >({
      query: ({
        organization_ids,
        author_ids,
        created_from,
        created_to,
        page,
        limit,
        column_key,
        order,
      }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          column_key,
          order,
        });

        organization_ids.forEach((organizationId) => {
          params.append('organization_ids', organizationId);
        });

        author_ids.forEach((authorId) => {
          params.append('author_ids', authorId);
        });

        if (created_from) {
          params.set('created_from', created_from);
        }

        if (created_to) {
          params.set('created_to', created_to);
        }

        return `/admin/community-analytics?${params.toString()}`;
      },
    }),
    getCommunityAnalyticsProjectSummary: builder.query<CommunityAnalyticsProjectSummary, string>({
      query: (projectId) => `/admin/community-analytics/${projectId}/summary`,
    }),
    getCommunityAnalyticsProjectSessions: builder.query<
      CommunityAnalyticsProjectSessionsResponse,
      CommunityAnalyticsProjectSessionsQuery
    >({
      query: ({ projectId, page, limit, column_key, order, created_from, created_to, statuses }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          column_key,
          order,
        });

        statuses.forEach((status) => params.append('status', status));

        if (created_from) {
          params.set('created_from', created_from);
        }

        if (created_to) {
          params.set('created_to', created_to);
        }

        return `/admin/community-analytics/${projectId}/sessions?${params.toString()}`;
      },
    }),
    getCommunityAnalyticsProjectSessionsReport: builder.query<
      Blob,
      CommunityAnalyticsProjectSessionsExportQuery
    >({
      query: ({ projectId, column_key, order, created_from, created_to, statuses, locale, time_zone }) => {
        const params = new URLSearchParams({ column_key, order, locale, time_zone });

        statuses.forEach((status) => params.append('status', status));

        if (created_from) {
          params.set('created_from', created_from);
        }

        if (created_to) {
          params.set('created_to', created_to);
        }

        return {
          url: `/admin/community-analytics/${projectId}/sessions/export?${params.toString()}`,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),
    getCommunityAnalyticsOrganizations: builder.query<
      CommunityAnalyticsOrganization[],
      void
    >({
      query: () => '/projects/community/organizations',
    }),
    getCommunityAnalyticsAuthors: builder.query<
      CommunityAnalyticsAuthor[],
      void
    >({
      query: () => '/users/authors',
    }),
    getCommunityAnalyticsReport: builder.query<
      Blob,
      CommunityAnalyticsExportQuery
    >({
      query: ({
        organization_ids,
        author_ids,
        created_from,
        created_to,
        locale,
      }) => {
        const params = new URLSearchParams();
        params.set('locale', locale);

        organization_ids.forEach((organizationId) => {
          params.append('organization_ids', organizationId);
        });

        author_ids.forEach((authorId) => {
          params.append('author_ids', authorId);
        });

        if (created_from) {
          params.set('created_from', created_from);
        }

        if (created_to) {
          params.set('created_to', created_to);
        }

        return {
          url: `/admin/community-analytics/export?${params.toString()}`,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    // Selects
    getOrganizationsSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/organizations',
      providesTags: ['Selects'],
    }),
    getRolesSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/roles',
      providesTags: ['Selects'],
    }),
    getSubscriptionPlansSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/subscription_plans',
      providesTags: ['Selects'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateEnabledUserMutation,
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useCreateOrganizationImageUploadTargetMutation,
  useDeleteOrganizationImageMutation,
  useGetSubscriptionPeriodsQuery,
  useGetSubscriptionPeriodQuery,
  useCreateSubscriptionPeriodMutation,
  useUpdateSubscriptionPeriodMutation,
  useDeleteSubscriptionPeriodMutation,
  useGetTariffUsageQuery,
  useGetManualDeductionsQuery,
  useCreateManualDeductionMutation,
  useUpdateManualDeductionMutation,
  useDeleteManualDeductionMutation,
  useGetActivityUsageQuery,
  useTransferProjectMutation,
  useGetReleaseNotesQuery,
  useGetReleaseNotesTableListQuery,
  useGetReleaseNoteQuery,
  useCreateReleaseNoteMutation,
  useUpdateReleaseNoteMutation,
  useDeleteReleaseNoteMutation,
  useGetReportGenerationSessionsQuery,
  useRunSessionReportGenerationMutation,
  useRerunSessionReportGenerationMutation,
  useCancelSessionReportGenerationMutation,
  useGetIdeasQuery,
  useGetIdeaQuery,
  useGetIdeaVotesQuery,
  useUpdateIdeaMutation,
  useDeleteIdeaMutation,
  useGetCommunityAnalyticsQuery,
  useGetCommunityAnalyticsProjectSummaryQuery,
  useGetCommunityAnalyticsProjectSessionsQuery,
  useLazyGetCommunityAnalyticsProjectSessionsReportQuery,
  useGetCommunityAnalyticsOrganizationsQuery,
  useGetCommunityAnalyticsAuthorsQuery,
  useLazyGetCommunityAnalyticsReportQuery,
  useGetOrganizationsSelectQuery,
  useGetRolesSelectQuery,
  useGetSubscriptionPlansSelectQuery,
} = api;
